import React, { useState, useRef } from 'react';
import { Box, Button, Typography, Paper, Alert, Stack, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import MonacoEditor from '../../components/MonacoEditor.js';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function LargeJsonViewer() {
  const [jsonInput, setJsonInput] = useState('// Upload a very large JSON file (>10MB) to view it efficiently.\n// This tool bypasses heavy UI components to prevent browser crashes.');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const editorRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);
      setError(null);
      try {
        const text = await file.text();
        setJsonInput(text);
      } catch (err) {
        setError('Failed to read file.');
      } finally {
        setIsLoading(false);
      }
    }
    event.target.value = null;
  };

  const handleFormat = () => {
    setIsLoading(true);
    // Formatting a massive JSON string blocks the main thread.
    // Use setTimeout to allow UI to render the loading state first.
    setTimeout(() => {
      try {
        const parsed = JSON.parse(jsonInput);
        const formatted = JSON.stringify(parsed, null, 2);
        setJsonInput(formatted);
        setError(null);
      } catch (e) {
        setError(`Formatting Error: ${e.message}`);
      } finally {
        setIsLoading(false);
      }
    }, 50);
  };

  const handleCopy = () => {
    if (editorRef.current) {
      const val = editorRef.current.getValue();
      navigator.clipboard.writeText(val).catch(e => setError('Failed to copy. Data might be too large.'));
    }
  };

  const handleClear = () => {
    setJsonInput('');
    setError(null);
  };

  return (
    <>
      <SEO
        title="Large JSON Viewer | Open Big JSON Files | UmbrellaCo"
        description="View and format massive JSON files online without crashing your browser. Optimized high-performance editor for huge JSON datasets."
        keywords="large json viewer, open big json file, huge json editor, view massive json online, high performance json viewer"
        url="/json-tools/large-viewer"
        jsonLd={createSoftwareAppSchema(
          'Large JSON Viewer',
          'High-performance viewer for massive JSON files (10MB+).',
          'https://umbrellaco.in/json-tools/large-viewer',
          ['Large File Viewer', 'High Performance Editor', 'JSON Utilities']
        )}
      />
      <ToolPageContainer
        title="Large JSON Viewer"
        description="An optimized, lightweight editor designed to open, view, and format massive JSON datasets without freezing the browser."
        disablePadding
      >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button component="label" variant="contained" startIcon={<UploadFileIcon />}>
            Upload Large File
            <input type="file" hidden onChange={handleFileUpload} accept=".json" />
          </Button>
          <Button onClick={handleFormat} startIcon={<FormatAlignLeftIcon />} disabled={isLoading}>Format</Button>
          <Button onClick={handleCopy} startIcon={<ContentCopyIcon />} disabled={isLoading}>Copy</Button>
          <Button onClick={handleClear} startIcon={<ClearIcon />} disabled={isLoading}>Clear</Button>
          {isLoading && <CircularProgress size={24} sx={{ ml: 2, alignSelf: 'center' }} />}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
      </Box>

      <Box sx={{ flexGrow: 1, px: 2, pb: 2, minHeight: 0 }}>
        <Paper variant="outlined" sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
          <MonacoEditor
            ref={editorRef}
            language="json"
            value={jsonInput}
            onChange={setJsonInput}
          />
        </Paper>
      </Box>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default LargeJsonViewer;
